/**
 * The DOM half of a FORGE jump (`forge-fab` 2.3): once the page shows, scroll the anchored section
 * into view, ring it briefly and focus the control that fixes it. `forgeGuide.ts`'s `jumpPage`
 * picks the page; `ForgeKey.tsx` navigates there and then calls {@link revealAnchor}.
 */
import { prefersReducedMotion } from "../../lib/prefersReducedMotion";
import { ANCHOR_ATTR, type BuildAnchor } from "./forgeGuide";

/** A control an edit control is made of, enabled. The hidden file input of an upload is skipped. */
export const FOCUSABLE = [
  'input:not([disabled]):not([type="file"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button.be-step:not([disabled])",
  "button.unit-sheet-mini:not([disabled])",
].join(", ");

/** A section's preferred control (＋ ADD, ＋ EQUIP, SELECT FRAME), marked where the sheet renders it. */
export const FOCUS_ATTR = "data-forge-focus";

/** The class that rings a section, and how long it stays. */
export const RING_CLASS = "forge-ring";
export const RING_MS = 1600;

/** How long a jump waits for the page to render the anchor before it gives up. */
const WAIT_MS = 2000;

/**
 * The control a jump focuses: the section's marked control when enabled, else its first enabled
 * stepper increment (the items are mostly room left to spend), else its first control.
 */
export function anchorFocusTarget(section: Element): HTMLElement | null {
  return (
    section.querySelector<HTMLElement>(`[${FOCUS_ATTR}]:not([disabled])`) ??
    section.querySelector<HTMLElement>('button.be-step[aria-label^="increment "]:not([disabled])') ??
    section.querySelector<HTMLElement>(FOCUSABLE)
  );
}

export function findAnchor(anchor: BuildAnchor): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[${ANCHOR_ATTR}="${anchor}"]`);
}

function waitForAnchor(anchor: BuildAnchor): Promise<HTMLElement | null> {
  const start = performance.now();
  return new Promise((resolve) => {
    const poll = () => {
      const el = findAnchor(anchor);
      if (el) return resolve(el);
      if (performance.now() - start > WAIT_MS) return resolve(null);
      requestAnimationFrame(poll);
    };
    poll();
  });
}

const ringTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

/**
 * Scroll the anchor into view (instantly under reduced motion), ring it for {@link RING_MS} and
 * focus its control without a second scroll. Resolves false when the anchor never rendered.
 */
export async function revealAnchor(anchor: BuildAnchor): Promise<boolean> {
  const el = await waitForAnchor(anchor);
  if (!el) return false;
  el.scrollIntoView({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
  // Restart the ring when the same section is shown twice in a row.
  el.classList.remove(RING_CLASS);
  void el.offsetWidth;
  el.classList.add(RING_CLASS);
  clearTimeout(ringTimers.get(el));
  ringTimers.set(
    el,
    setTimeout(() => el.classList.remove(RING_CLASS), RING_MS),
  );
  anchorFocusTarget(el)?.focus({ preventScroll: true });
  return true;
}
